import React, { useEffect, useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { Button, Skeleton, Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useFetchOrgsList, useFetchMembersByOrganizationId } from 'hooks/use-admin'
import { useImpersonatingAuthToken } from 'hooks/use-auth-token'
import { useFetchAuthenticatedUser } from 'hooks/use-authenticated-user'
import { toastAtom } from 'state'
import { AuthImpersonate, OrganizationFull, User } from 'api/model'
import { adminImpersonate } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useActiveOrganization, useImpersonatingOrganization } from 'hooks/use-organization'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import Selector from 'components/Selector'

const Impersonate = () => {
  const isMobile = useCheckMobileScreen()

  const { adminOrgs } = useFetchOrgsList()
  const { user } = useFetchAuthenticatedUser()
  const [, setAuthToken] = useImpersonatingAuthToken()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeOrganization] = useActiveOrganization()
  const [, setImpersonatingOrganization] = useImpersonatingOrganization()

  const [org, setOrg] = useState<OrganizationFull | null>(null)

  useEffect(() => {
    if (adminOrgs && !org) {
      setOrg(adminOrgs.find((o) => o.id === activeOrganization) || null)
    }
  }, [adminOrgs, org, setOrg, activeOrganization])

  const { members } = useFetchMembersByOrganizationId(org?.id ?? '')

  // when org changes, we should reset the member id
  const [member, setMember] = useState<User | null>(null)
  useEffect(() => {
    if (!member && members.length > 0) {
      setMember(members[0])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members])

  // whenever the org changes and userId is present, we should reset it
  useEffect(() => {
    if (member) {
      setMember(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org])

  const setToast = useSetRecoilState(toastAtom)

  const onLoginClicked = async () => {
    setIsSubmitting(true)

    if (!org || !member) {
      setIsSubmitting(false)
      return
    }

    try {
      const input: AuthImpersonate = {
        organization_id: org.id,
        user_id: member.id,
      }

      const result = await adminImpersonate(input)

      setAuthToken(result.data.token)
      setImpersonatingOrganization(org.id)

      // hard refresh
      window.location.pathname = ''
    } catch (Error) {
      setToast({ title: 'Impersonation attempt failed', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderContent = () => {
    if (!adminOrgs || members.length === 0) {
      // TODO handle empty state
      return <Loading />
    }
    return (
      <Stack spacing={4} sx={{ width: isMobile ? '100%' : '376px' }}>
        <Selector
          autoComplete={adminOrgs.find((o) => o.id === org?.id)?.email_domain}
          id="impersonate-organization-selector"
          label="Organization"
          option={org}
          options={adminOrgs}
          setOption={setOrg}
          isDarkBackdrop={false}
          width="240px"
          optionProperties={['email_domain']}
        />

        <Selector
          id="impersonate-user-selector"
          label="User"
          option={member}
          options={members}
          setOption={setMember}
          isDarkBackdrop={true}
          width="240px"
          optionProperties={['name', 'email']}
        />

        <Button
          variant="contained"
          size="large"
          sx={{ width: isMobile ? '100%' : '240px' }}
          disabled={isSubmitting || user?.id === member?.id}
          onClick={onLoginClicked}>
          Login as the user
        </Button>
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        mt: isMobile ? 2 : 0,
      }}>
      {!isMobile && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4">Customer account access</Typography>
        </Stack>
      )}
      {renderContent()}
    </Box>
  )
}

const Loading = () => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack spacing={4} sx={{ width: isMobile ? '100%' : '376px' }}>
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
    </Stack>
  )
}

export default Impersonate
